import { useTranslation } from 'react-i18next';
import type { Id, Member, MemberType } from '@hero/schema';
import { useMemberCreateAdapter } from '@hero/web-hooks';
import type { MemberFormValues } from '@hero/web-hooks';
import { ModalForm } from '../../../app/Modal';
import { MemberForm } from '../Form/MemberForm';
import { toast } from 'sonner';

export interface MemberEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When set, edit this member; when undefined, create mode. */
  memberId?: Id;
  displayForUser?: 'customer' | 'carrier';
  onMemberSaved?: (member?: Member) => void;
  /** Lock the member type (e.g. 'driver') so user can't change it. */
  forceType?: MemberType;
  /** Pre-fill form values. */
  defaultValues?: Partial<MemberFormValues>;
}

export function MemberEditModal({
  isOpen,
  onClose,
  memberId,
  displayForUser,
  onMemberSaved,
  forceType,
  defaultValues,
}: MemberEditModalProps) {
  const { t } = useTranslation(['m-member', 'c-main']);

  const mergedDefaults = forceType ? { type: forceType, ...defaultValues } : defaultValues;

  const {
    memberQuery,
    createMember,
    updateMember,
    formHook,
    reset,
    onSaveClick,
    isEdit,
    isPhoneLocked,
    phoneChange,
    accountRename,
  } = useMemberCreateAdapter({
      memberId,
      defaultValues: mergedDefaults,
      onMemberSaved: member => {
        toast.success(
          isEdit ? t('toast.success.member_updated') : t('toast.success.member_created'),
        );
        onMemberSaved?.(member);
        handleClose();
      },
      onError: error => {
        toast.error(
          isEdit
            ? t('toast.error.member_update', { message: error.message })
            : t('toast.error.member_create', { message: error.message }),
        );
      },
    });

  const handleClose = () => {
    reset();
    onClose();
  };

  const isLoadingForm = isEdit && memberQuery.isLoading;
  const isLoading = createMember.isPending || updateMember.isPending;
  const isSuccess = createMember.isSuccess || updateMember.isSuccess;

  return (
    <ModalForm
      isOpen={isOpen}
      onClose={handleClose}
      onSave={onSaveClick}
      title={isEdit ? t('modal.edit_member_title') : t('modal.create_member_title')}
      description={
        isEdit ? t('modal.edit_member_description') : t('modal.create_member_description')
      }
      isLoading={isLoading}
      isLoadingForm={isLoadingForm}
      isSuccess={isSuccess}
      size="lg"
      saveText={isEdit ? t('c-main:crud_buttons.update') : t('c-main:crud_buttons.create')}
      disableSave={!formHook.isValid}>
      <MemberForm
        formHook={formHook}
        isLoading={isLoading}
        hideSubmitButton
        displayForUser={displayForUser}
        forceType={forceType}
        isPhoneLocked={isPhoneLocked}
        phoneChange={phoneChange}
        accountRename={accountRename}
      />
    </ModalForm>
  );
}
